import { useEffect } from 'react';
import { useTheme } from '@shopify/restyle';
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import type { Theme } from 'app/shopify';
import { UNDO_DURATION_MS } from './request-queue';

function elapsedProgress(deadline: number, now: number) {
  return Math.max(0, Math.min(1, 1 - (deadline - now) / UNDO_DURATION_MS));
}

export function RequestProgressFill({ deadline, now }: { deadline: number; now: number }) {
  const theme = useTheme<Theme>();
  const reducedMotion = useReducedMotion();
  const progress = useSharedValue(elapsedProgress(deadline, now));

  useEffect(() => {
    if (reducedMotion) return;
    const currentTime = Date.now();
    progress.set(elapsedProgress(deadline, currentTime));
    progress.set(withTiming(1, { duration: Math.max(0, deadline - currentTime), easing: Easing.linear }));
    return () => cancelAnimation(progress);
  }, [deadline, progress, reducedMotion]);

  const fillStyle = useAnimatedStyle(() => ({ width: `${progress.get() * 100}%` as `${number}%` }), [progress]);

  return (
    <Animated.View
      testID="grouped-request-progress-fill"
      pointerEvents="none"
      accessible={false}
      style={[
        { position: 'absolute', top: 0, bottom: 0, left: 0, backgroundColor: theme.colors['surface.secondary'] },
        reducedMotion ? { width: `${elapsedProgress(deadline, now) * 100}%` } : fillStyle,
      ]}
    />
  );
}
